<script setup lang="ts" generic="T">
import greenInfoIcon from '@/assets/icons/green/info.svg'
import { computed, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { MessageSchema } from '@/plugins/i18n'
import Tooltip from './Tooltip.vue'
import { useMenuPosition } from '@/composables/useMenuPosition'

defineOptions({ inheritAttrs: false })
const { openMenu, closeMenu } = useMenuPosition()
const { t } = useI18n<MessageSchema>()

type SelectValue = string | number | boolean | undefined

const props = withDefaults(
  defineProps<{
    label: string
    id?: string
    required?: boolean
    items: unknown[]
    modelValue?: SelectValue
    labelField?: keyof T | string
    valueField?: keyof T | string
    loading?: boolean
    error?: boolean | string
    emptyState?: boolean
    placeholder?: string
    disabled?: boolean
    tooltip?: string | null
    description?: string | null
    density?: 'default' | 'comfortable' | 'compact'
    clearable?: boolean
    persistentHint?: boolean
  }>(),
  {
    id: '',
    required: false,
    labelField: 'label',
    valueField: 'value',
    density: 'default',
    modelValue: undefined,
    loading: false,
    error: false,
    emptyState: true,
    placeholder: undefined,
    disabled: false,
    tooltip: undefined,
    description: undefined,
    clearable: false,
    persistentHint: false,
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', v: SelectValue): void
}>()

const { required, items } = toRefs(props)

const value = computed<SelectValue>({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const getItemValue = (item: T): SelectValue =>
  item[props.valueField as keyof T] as SelectValue

const selectedItemLabel = computed(() => {
  const selectedItem = items.value?.find(
    (item: T) => getItemValue(item) === value.value
  )
  return selectedItem
    ? selectedItem[props.labelField as keyof T]
    : props.placeholder || t('general.pickAnOption')
})
</script>

<template>
  <v-select
    :id="id"
    v-model="value"
    variant="outlined"
    :density="props.density"
    class="w-100"
    style="min-width: 0"
    :items="items"
    :item-title="props.labelField as string"
    :item-value="props.valueField as string"
    :error="Boolean(props.error)"
    :error-messages="typeof props.error === 'string' ? props.error : ''"
    :hide-details="!props.error && props.persistentHint === false"
    :hint="props.description ?? undefined"
    :disabled="props.loading || props.disabled"
    :placeholder="placeholder"
    :required="required"
    :clearable="clearable"
    :persistent-hint="props.persistentHint"
  >
    <template #label>
      <div class="d-flex align-center justify-space-between w-100">
        <span
          class="text-body-2 font-weight-medium"
          :class="{
            'text-error': props.error,
            'text-grey': props.disabled && !props.error,
          }"
        >
          {{ props.label }}
        </span>
      </div>
    </template>

    <template #selection>
      <span class="text-truncate" :title="selectedItemLabel?.toString()">
        {{ selectedItemLabel }}
      </span>
    </template>

    <template v-if="tooltip" #append>
      <div class="tooltip-wrapper">
        <Tooltip :header-text="tooltip">
          <img
            id="tooltip"
            width="24"
            :src="greenInfoIcon"
            alt="info"
            @mouseenter="openMenu"
            @mouseleave="closeMenu"
          />
        </Tooltip>
      </div>
    </template>
  </v-select>
</template>

<style scoped>
.text-truncate {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tooltip-wrapper {
  position: relative;
}
.tooltip-content {
  position: absolute;
  z-index: 50;
  margin-top: 8px;
  width: max-content;
  max-width: 30rem;
  background-color: #424242;
  color: #fff;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
</style>
